import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormArray,
  AbstractControl,
  Validators
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { NumberRangeValidator } from 'src/app/shared/validators/number-range.validator';
import { AttributesEditorDataSource } from './attributesEditorDataSource';
import { EditableAttribute } from 'src/app/shared/models/attributes/editableAttribute';

import _ from 'lodash';
import { AttributeDefinitionCondition } from 'src/app/shared/models/attributes/attributeDefinitionCondition';
import { MultiselectEvent, MultiselectItem } from '../multiselect/multiselect.component';
import { CustomDocumentUploadFile } from '../custom-document-uploader/custom-document-upload-file';
import { AttributeEditorMode } from 'src/app/shared/models/attributes/attributeEditorMode';
import { MatDialog } from '@angular/material/dialog';
import { ComplexAttributeWrite } from 'src/app/shared/models/attributes/complexAttributeWrite';
import {
  ComplexAttributeItems,
  FlatComplexAttributeValue
} from 'src/app/shared/models/attributes/flatAttribute';
import { ComplexAttributeSubFieldValue } from 'src/app/shared/models/attributes/complexAttributeSubFieldValue';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { DateRangeValidator } from 'src/app/shared/validators/date-range.validator';
import { ApplicationFormPage } from 'src/app/shared/models/application/formPage';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { ComplexFieldEditorComponent } from 'src/shared-app-portal/components/complex-field-editor/complex-field-editor.component';
import { ModalConfirmationComponent } from 'src/shared-app-portal/components/modal-confirmation/modal-confirmation.component';
import { AccessibilityService } from 'src/app/shared/services/accessibility.service';
import { CustomDocumentService } from 'src/app/shared/services/custom-document.service';

@Component({
  selector: 'attributes-editor',
  templateUrl: 'attributes-editor.component.html',
  styleUrls: ['attributes-editor.component.scss'],
  standalone: false
})
export class AttributesEditorComponent {
  private _dataSource: AttributesEditorDataSource = new AttributesEditorDataSource();
  private _dataSourceInitialized: boolean = false;
  @Input() mode: AttributeEditorMode = AttributeEditorMode.Application;
  showOtherInput: Record<string, boolean> = {};

  @Input()
  set dataSource(value: AttributesEditorDataSource) {
    this._dataSource = value;
    if (this._dataSource) {
      this._dataSource.editableAttributes.subscribe((attributes) => {
        if (attributes && !this._dataSourceInitialized) {
          this._dataSource.editable
            .filter((x) => x.conditions && x.conditions.length > 0)
            .forEach((attribute) => {
              this.isVisible(attribute);
            });
          this.setupPage();
          this.setupEditAttributesForm();
          this._dataSourceInitialized = true;
        }
      });
    }
  }
  @Output() blurred = new EventEmitter<boolean>();
  get dataSource(): AttributesEditorDataSource {
    return this._dataSource;
  }

  @Input() fxFlexWidth: string = '100%';
  @Input() useAttributeDescription: boolean = false;
  @Input() onTeamForm: boolean = false;
  @Input() secureLinkId: string = '';
  @Input() isUploading: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() entityId = '';
  @Input() formPage: ApplicationFormPage;
  // Only relevant for form-fill contexts (Application/Survey/Evaluation). Encrypted values can
  // only be revealed on private forms; leave unset (falsy) for Person/Team profile contexts
  // where reveal permission is instead gated solely by isProfileEditorMode().
  @Input() isPrivate: boolean = false;

  @Output() fieldUpdated: EventEmitter<string> = new EventEmitter<string>();
  @Output() downloadFile: EventEmitter<{ fileName: string; attributeDefinitionId: string }> =
    new EventEmitter<{ fileName: string; attributeDefinitionId: string }>();

  @Output() addOrUpdateComplexValue: EventEmitter<{
    attributeDefinitionId: string;
    writeModel: ComplexAttributeWrite[];
  }> = new EventEmitter<{ attributeDefinitionId: string; writeModel: ComplexAttributeWrite[] }>();

  attributesForm: UntypedFormGroup;
  editAttributes = [];

  get isValid(): boolean {
    return this.attributesForm && this.attributesForm.valid;
  }

  get personDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.PersonDetails;
  }

  get teamDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.TeamDetails;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private attributeService: AttributeService,
    private customDocumentService: CustomDocumentService,
    private accessibilityService: AccessibilityService
  ) {}

  isProfileEditorMode(): boolean {
    return (
      this.mode == AttributeEditorMode.PersonDetails || this.mode == AttributeEditorMode.TeamDetails
    );
  }

  assignValueToAttribute(attribute: EditableAttribute) {
    if (
      attribute.isDateType() &&
      this.attributesForm.get(attribute.attributeDefinitionId).errors &&
      this.attributesForm.get(attribute.attributeDefinitionId).hasError('matDatepickerParse')
    ) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        invaliddate: attribute.name + ' must be a valid date in mm/dd/yyyy format.'
      });
      attribute.setOrAddValue(EditableAttribute.INVALID_DATE_VALUE);
      return;
    }

    let originalValue =
      typeof attribute.value === undefined ||
      attribute.value === EditableAttribute.INVALID_DATE_VALUE
        ? null
        : attribute.value;

    let currentValue = this.attributesForm.get(attribute.attributeDefinitionId).value;

    if (
      attribute.isEncryptedFieldType() &&
      (currentValue == '***' || currentValue == attribute.decryptedValue)
    )
      return;

    let attributeChanged: boolean = false;

    if (
      (originalValue === undefined || originalValue === null) &&
      (currentValue === undefined || currentValue === null)
    ) {
      attributeChanged = false;
    } else {
      attributeChanged = originalValue !== currentValue;
    }

    attribute.edited = attribute.edited || attributeChanged;

    if (attributeChanged) {
      attribute.setOrAddValue(currentValue);
      if (attribute.isEncryptedFieldType()) {
        // Reflect the just-saved plaintext as the "revealed" value and mark the control
        // pristine again — otherwise it stays dirty forever after the first edit, which
        // permanently hides the reveal/hide icon (its *ngIf gates on !dirty).
        attribute.decryptedValue = currentValue;
        this.attributesForm.get(attribute.attributeDefinitionId)?.markAsPristine();
      }
      if (attribute.isDropDownType() || attribute.isRadioButtonGroupType()) {
        this.updateQuestionsVisibility(attribute);
      }
    }
    this.blurred.emit(attributeChanged);
  }

  private revealedAttributeIds = new Set<string>();

  isRevealed(attribute: any): boolean {
    return this.revealedAttributeIds.has(attribute.attributeDefinitionId);
  }

  togglePasswordVisibility(attribute: any): void {
    const id = attribute.attributeDefinitionId;
    if (this.isRevealed(attribute)) {
      this.revealedAttributeIds.delete(id);
      attribute.decryptedValue = null;
      this.attributesForm.get(id)?.setValue('***');
      return;
    }

    this.attributeService.decryptAttribute(id, attribute.value).subscribe((result) => {
      if (result) {
        this.revealedAttributeIds.add(id);
        attribute.decryptedValue = result;
        this.attributesForm.get(id)?.setValue(result);
        if (!attribute.isReadOnly) {
          this.attributesForm.get(id)?.enable();
        }
      }
    });
  }

  private setupPage() {
    if (this.formPage) {
      this.formPage.editableAttributes = [];
      let attrIds = this._dataSource.attributeDefinitions
        .filter((x) => x.formPageId == this.formPage.id)
        .map((y) => y.id);
      this._dataSource.editable?.forEach((attr) => {
        if (attrIds.includes(attr.attributeDefinitionId)) {
          this.formPage.editableAttributes.push(attr);
        }
      });
    } else {
      this.formPage = new ApplicationFormPage();
      this.formPage.editableAttributes = this._dataSource.editable;
    }
  }

  public validateSubFieldsInComplexFields() {
    this._dataSource.editable
      .filter((a) => a.isComplexType() && !a.isReadOnly)
      .forEach((a) => {
        this.addComplexSubFieldRequiredError(a);
      });
  }

  private setupEditAttributesForm() {
    const form = {};

    this._dataSource.editable.forEach((attribute: Attribute) => {
      let validators = [];
      let value = undefined;
      if (attribute.required) {
        validators.push(NotEmptyValidator(`${attribute.name} is required`));
      }
      if (attribute.isNumberType()) {
        let attributeDefinition = this.getAttributeDefinitionById(attribute.attributeDefinitionId);
        validators.push(
          NumberRangeValidator(
            parseInt(attributeDefinition.min),
            parseInt(attributeDefinition.max),
            `${attribute.name}`
          )
        );
      }

      if (attribute.isDateRangeType()) {
        validators.push(DateRangeValidator(`${attribute.value}`, `${attribute.name}`));
      }

      if (attribute.isCheckboxType()) {
        value = this.getCheckboxes(attribute);
      } else {
        value = attribute.getValue();
      }
      // mat-radio-group matches form value to options by strict identity, so the
      // form value must be the same object reference that the radio buttons render.
      // The cloned saved value list item is a different reference from the definition's
      // list, so look up the matching definition item by id.
      if (attribute.isRadioButtonGroupType() && value) {
        const definition = this.getAttributeDefinitionById(attribute.attributeDefinitionId);
        const savedId = (value as AttributeDefinitionValueListItem).id;
        const matched = definition?.valueList?.find((item) => item.id === savedId);
        if (matched) {
          value = matched;
          attribute.attributeDefinitionValueListItem = matched;
          attribute.defaultValue = matched;
        }
      }
      attribute.defaultValue = attribute.getValue();
      if (attribute.type == 'EncryptedField' && value) {
        // Masked encrypted values must remain editable everywhere — including Person/Team
        // Details and profile pages — so users can overwrite the value without first
        // revealing it. Read-only questions are handled separately by the
        // `!attribute.isReadOnly` template split above this branch.
        form[attribute.attributeDefinitionId] = [{ value: '***', disabled: false }, validators];
      } else {
        form[attribute.attributeDefinitionId] = [value, validators];
      }
    });

    this.attributesForm = this.formBuilder.group(form);

    // Pre-existing over-max checkbox selections (max lowered after data was saved) must
    // block save from the start of the edit session without mutating the saved data.
    this._dataSource.editable
      .filter((a) => a.isCheckboxType() && !a.isReadOnly)
      .forEach((a) => this.addCheckboxMaxSelectionsError(a));
  }

  getAttributeDefinitionById(id): AttributeDefinition {
    return _.find(
      this._dataSource.attributeDefinitions,
      (attributeDefinition) => attributeDefinition.id === id
    );
  }

  addComplexRangeError(attribute: EditableAttribute) {
    if (
      this.mode == AttributeEditorMode.Application &&
      attribute.required &&
      attribute._min &&
      attribute.complexAttributeValues.length < Number(attribute._min)
    ) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        ...this.attributesForm.controls[attribute.attributeDefinitionId].errors,
        complexRange: attribute.name + ' must have at least ' + attribute._min + ' entries.'
      });
    } else if (
      this.mode == AttributeEditorMode.Application &&
      attribute._min &&
      attribute.complexAttributeValues.length > 0 &&
      attribute.complexAttributeValues.length < Number(attribute._min)
    ) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        ...this.attributesForm.controls[attribute.attributeDefinitionId].errors,
        complexRange: `${attribute.name} must have no entries, or at least ${attribute._min} entries.`
      });
    } else {
      this.clearSpecificError(
        this.attributesForm.controls[attribute.attributeDefinitionId],
        'complexRange'
      );
    }
  }

  addComplexSubFieldRequiredError(attribute: EditableAttribute) {
    const hasInvalidComplexValues =
      attribute.complexAttributeValues?.length > 0 &&
      !attribute.complexAttributeValues.every((cv) => attribute.isComplexValueValid(cv));

    if (this.mode == AttributeEditorMode.Application && hasInvalidComplexValues) {
      this.attributesForm.controls[attribute.attributeDefinitionId].setErrors({
        ...this.attributesForm.controls[attribute.attributeDefinitionId].errors,
        complexSubFieldRequired: attribute.name + ' has at least one invalid entry.'
      });
    } else {
      this.clearSpecificError(
        this.attributesForm.controls[attribute.attributeDefinitionId],
        'complexSubFieldRequired'
      );
    }
  }

  isComplexAttributeValueValid(
    attribute: EditableAttribute,
    flatAttribute: FlatComplexAttributeValue
  ): boolean {
    if (this.mode != AttributeEditorMode.Application) return true;
    return attribute.isComplexValueValid(flatAttribute);
  }

  private clearSpecificError(control: AbstractControl | null, errorKey: string) {
    if (!control || !control.errors) return;
    const errors = { ...(control.errors || {}) };
    delete errors[errorKey];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  isCheckboxDisabled(
    attribute: Attribute,
    valueListItem: AttributeDefinitionValueListItem
  ): boolean {
    const editableAttribute = _.find(
      this._dataSource.editable,
      (personAttribute) => personAttribute.attributeDefinitionId == attribute.attributeDefinitionId
    );
    const max = editableAttribute?.maxSelections;
    if (max == null) {
      return false;
    }
    // Only disable unchecked items — already-selected (even over-max legacy) items stay
    // enabled so the user can uncheck them.
    return (
      editableAttribute.valueList.length >= max && !this.getCheckboxStatus(attribute, valueListItem)
    );
  }

  addCheckboxMaxSelectionsError(attribute: EditableAttribute) {
    const control = this.attributesForm.controls[attribute.attributeDefinitionId];
    if (!control) {
      return;
    }
    if (attribute.exceedsMaxSelections) {
      control.setErrors({
        ...control.errors,
        maxSelections: `${attribute.name}: select no more than ${attribute.maxSelections} option${
          attribute.maxSelections == 1 ? '' : 's'
        }.`
      });
    } else {
      this.clearSpecificError(control, 'maxSelections');
    }
  }

  attributeComplex(attribute: EditableAttribute): boolean {
    if (this.attributesForm && this.attributesForm.controls[attribute.attributeDefinitionId]) {
      this.addComplexRangeError(attribute);
    }
    return attribute._max
      ? attribute.complexAttributeValues.length >= Number(attribute._max)
      : false;
  }

  getCheckboxes(attribute: Attribute): UntypedFormArray {
    const comp = this;
    let checkboxControls = comp.formBuilder.array([]);
    const checkboxAttributeDefinition = _.find(
      this._dataSource.attributeDefinitions,
      (attrDef: AttributeDefinition) => attrDef.id == attribute.attributeDefinitionId
    );
    const selectedValues = _.flatten(
      _.filter(
        this._dataSource.editable,
        (attr: Attribute) => attr.attributeDefinitionId == attribute.attributeDefinitionId
      ).map((attr) => attr.valueList)
    );

    _.forEach(
      checkboxAttributeDefinition.valueList,
      (valueListItem: AttributeDefinitionValueListItem) => {
        const selectedAttribute = _.find(selectedValues, (v) => v.id == valueListItem.id);

        let item = {
          id: valueListItem.id,
          checked: selectedAttribute ? true : false
        };

        checkboxControls.push(comp.formBuilder.control(item));
      }
    );

    return checkboxControls;
  }

  markAllAsTouched() {
    this._dataSource.editable.forEach((attr) => {
      if (attr.isVisible) {
        if (attr.needsOtherCheck) {
          this.attributesForm.get(`${attr.attributeDefinitionId}_other`)?.markAsTouched();
        }
        if (attr.isCheckboxType()) {
          this.attributesForm.get(attr.attributeDefinitionId).value.touched = true;
        } else {
          this.attributesForm.get(attr.attributeDefinitionId).markAsTouched();
        }
      }
    });
  }

  nonValidAttributes(): Map<string, AbstractControl> {
    let nonValidAttributes: Map<string, AbstractControl> = new Map<string, AbstractControl>();
    if (this._dataSource.editable) {
      this._dataSource.editable.forEach((attr) => {
        if (attr.isVisible && !attr.isValid) {
          nonValidAttributes.set(
            attr.attributeDefinitionId,
            this.attributesForm.get(attr.attributeDefinitionId)
          );
        }
      });
    }
    return nonValidAttributes;
  }

  nonValidAttributesInPage(page: ApplicationFormPage): Map<string, AbstractControl> {
    let nonValidAttributes: Map<string, AbstractControl> = new Map<string, AbstractControl>();
    if (page.editableAttributes) {
      page.editableAttributes.forEach((attr) => {
        if ((attr.isVisible && !attr.isValid) || (attr.needsOtherCheck && !attr.otherValue)) {
          nonValidAttributes.set(
            attr.attributeDefinitionId,
            this.attributesForm.get(attr.attributeDefinitionId)
          );
        }
      });
    }
    return nonValidAttributes;
  }

  isAttributeTouched(attr) {
    return this.attributesForm.get(attr.attributeDefinitionId).touched;
  }

  onDropdownChange(event: any, attribute: EditableAttribute) {
    this.validateDropdownOtherAttribute(event.value?.itemValue, attribute);
  }

  clearRadioButtonGroup(attribute: EditableAttribute) {
    const control = this.attributesForm.get(attribute.attributeDefinitionId);
    if (!control) return;
    control.setValue(null);
    attribute.setOrAddValue(null);
    attribute.edited = true;
    // Tear down any "Other" textbox/error that the previous selection may have created.
    const otherControlName = this.otherInputControlName(attribute);
    if (this.attributesForm.contains(otherControlName)) {
      this.AddOrRemoveOtherControl(otherControlName, false);
    }
    attribute.otherValue = null;
    this.showOtherInput[attribute.attributeDefinitionId] = false;
    const currentErrors = { ...(control.errors || {}) };
    delete currentErrors['otherRequired'];
    control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    this.updateQuestionsVisibility(attribute);
    this.onFieldChanged(attribute.attributeDefinitionId);
    this.blurred.emit(true);
  }

  private validateDropdownOtherAttribute(selected: any, attribute: EditableAttribute) {
    if (!selected) return;
    const otherControlName = `${attribute.attributeDefinitionId}_other`;
    const control = this.attributesForm.controls[attribute.attributeDefinitionId];

    if (selected.toLowerCase() === 'other' && attribute.attributeDefinitionValueListItem?.isOther) {
      this.AddOrRemoveOtherControl(otherControlName, true);

      // Always set error only for THIS control
      if (!attribute.otherValue) {
        const currentErrors = { ...(control.errors || {}) };
        currentErrors['otherRequired'] =
          `You selected "Other" for ${attribute.name}. Please specify your answer`;
        control.setErrors(currentErrors);
      } else {
        this.attributesForm.get(otherControlName)?.setValue(attribute.otherValue);
      }
      // Subscribe to this otherControl only, to clear only this control's error when input is changed
      this.attributesForm.get(otherControlName)?.valueChanges.subscribe((inputValue) => {
        if (inputValue == attribute.otherValue) return;
        const currentErrors = { ...(control.errors || {}) };
        if (inputValue) {
          delete currentErrors['otherRequired'];
          attribute.otherValue = inputValue;
          attribute.edited = true;
        } else {
          attribute.otherValue = null;
          currentErrors['otherRequired'] =
            `You selected "Other" for ${attribute.name}. Please specify your answer`;
        }
        control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
      });
      this.showOtherInput[attribute.attributeDefinitionId] = true;
    } else {
      if (this.attributesForm.contains(otherControlName)) {
        this.AddOrRemoveOtherControl(otherControlName, false);
        attribute.otherValue = null;
      }
      this.showOtherInput[attribute.attributeDefinitionId] = false;
      // Only clear for THIS dropdown's error
      const currentErrors = { ...(control.errors || {}) };
      delete currentErrors['otherRequired'];
      control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  private AddOrRemoveOtherControl(otherControlName: string, add: boolean) {
    // Before adding a new control:
    const controlErrors: { [key: string]: any } = {};

    // Capture errors for all existing controls
    Object.keys(this.attributesForm.controls).forEach((key) => {
      const ctrl = this.attributesForm.get(key);
      if (ctrl && ctrl.errors) {
        controlErrors[key] = { ...ctrl.errors };
      }
    });

    if (add) {
      // Add the control (this may clear errors on existing controls)
      if (!this.attributesForm.contains(otherControlName)) {
        this.attributesForm.addControl(
          otherControlName,
          this.formBuilder.control('', Validators.required)
        );
      }
    } else if (this.attributesForm.contains(otherControlName)) {
      this.attributesForm.removeControl(otherControlName);
    }

    // Restore errors for all controls (except for the new one, unless needed)
    Object.keys(controlErrors).forEach((key) => {
      const ctrl = this.attributesForm.get(key);
      if (ctrl) {
        ctrl.setErrors(controlErrors[key]);
      }
    });
  }

  // Utility to get the control name for the other input, e.g., appending '_other' to the id
  otherInputControlName(attribute: EditableAttribute): string {
    return `${attribute.attributeDefinitionId}_other`;
  }

  isOtherTextShown(attribute: EditableAttribute): boolean {
    if (
      this.attributesForm &&
      attribute.otherValue &&
      this.attributesForm.get(attribute.attributeDefinitionId) &&
      !this.attributesForm.get(this.otherInputControlName(attribute))
    ) {
      if (attribute.isMultiSelectType() || attribute.isCheckboxType()) {
        this.validateMultiselectOtherAttribute(
          attribute.valueList?.find((item) => item.isOther)?.itemValue,
          attribute,
          true
        );
      } else {
        this.validateDropdownOtherAttribute(
          attribute.attributeDefinitionValueListItem?.itemValue,
          attribute
        );
      }
    }
    return this.showOtherInput[attribute.attributeDefinitionId];
  }

  onCheckboxChange(
    event: any,
    valueListItem: AttributeDefinitionValueListItem,
    attribute: EditableAttribute
  ) {
    this.validateCheckboxOtherAttribute(valueListItem?.itemValue, attribute, event?.checked);
  }

  private validateCheckboxOtherAttribute(
    selected: any,
    attribute: EditableAttribute,
    checked?: boolean
  ) {
    if (!selected || selected.toLowerCase() !== 'other' || checked === undefined) return;
    const otherControlName = this.otherInputControlName(attribute);
    const control = this.attributesForm.get(attribute.attributeDefinitionId);

    if (checked === true && attribute.valueList?.filter((item) => item.isOther).length > 0) {
      this.AddOrRemoveOtherControl(otherControlName, true);

      // Subscribe to this otherControl only, to clear only this control's error when input is changed
      const otherControl = this.attributesForm.get(otherControlName);
      otherControl.valueChanges.subscribe((inputValue) => {
        if (inputValue == attribute.otherValue) return;
        const currentErrors = { ...(control.errors || {}) };
        if (inputValue) {
          delete currentErrors['otherRequired'];
          attribute.otherValue = inputValue;
          attribute.edited = true;
        } else {
          attribute.otherValue = null;
          currentErrors['otherRequired'] =
            `You selected "Other" for ${attribute.name}. Please specify your answer`;
        }
        control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
      });

      // Always set error only for THIS control
      if (!attribute.otherValue) {
        const currentErrors = { ...(control.errors || {}) };
        currentErrors['otherRequired'] =
          `You selected "Other" for ${attribute.name}. Please specify your answer`;
        control.setErrors(currentErrors);
      } else {
        this.attributesForm.get(otherControlName)?.setValue(attribute.otherValue);
      }

      this.showOtherInput[attribute.attributeDefinitionId] = true;
    } else {
      if (this.attributesForm.contains(otherControlName)) {
        this.AddOrRemoveOtherControl(otherControlName, false);
        attribute.otherValue = null;
      }
      this.showOtherInput[attribute.attributeDefinitionId] = false;
      // Only clear for THIS dropdown's error
      const currentErrors = { ...(control.errors || {}) };
      delete currentErrors['otherRequired'];
      control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  onMultiselectChange(event: any, attribute: EditableAttribute) {
    this.validateMultiselectOtherAttribute(
      event.changedValue?.name,
      attribute,
      event.changedValue?.selected
    );
  }

  private validateMultiselectOtherAttribute(
    selected: any,
    attribute: EditableAttribute,
    checked?: boolean
  ) {
    if (!selected || selected.toLowerCase() !== 'other' || checked === undefined) return;
    const otherControlName = `${attribute.attributeDefinitionId}_other`;
    const control = this.attributesForm.get(attribute.attributeDefinitionId);

    if (checked === true && attribute.valueList?.filter((item) => item.isOther).length > 0) {
      this.AddOrRemoveOtherControl(otherControlName, true);

      // Subscribe to this otherControl only, to clear only this control's error when input is changed
      const otherControl = this.attributesForm.get(otherControlName);
      otherControl.valueChanges.subscribe((inputValue) => {
        if (inputValue == attribute.otherValue) return;
        const currentErrors = { ...(control.errors || {}) };
        if (inputValue) {
          delete currentErrors['otherRequired'];
          attribute.otherValue = inputValue;
          attribute.edited = true;
        } else {
          attribute.otherValue = null;
          currentErrors['otherRequired'] =
            `You selected "Other" for ${attribute.name}. Please specify your answer`;
        }
        control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
      });

      // Always set error only for THIS control
      if (!attribute.otherValue) {
        const currentErrors = { ...(control.errors || {}) };
        currentErrors['otherRequired'] =
          `You selected "Other" for ${attribute.name}. Please specify your answer`;
        control.setErrors(currentErrors);
      } else {
        this.attributesForm.get(otherControlName)?.setValue(attribute.otherValue);
      }

      this.showOtherInput[attribute.attributeDefinitionId] = true;
    } else {
      if (this.attributesForm.contains(otherControlName)) {
        this.AddOrRemoveOtherControl(otherControlName, false);
        attribute.otherValue = null;
      }
      this.showOtherInput[attribute.attributeDefinitionId] = false;
      // Only clear for THIS dropdown's error
      const currentErrors = { ...(control.errors || {}) };
      delete currentErrors['otherRequired'];
      control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  longAnswerChanged(attribute: Attribute, text: string) {
    attribute.value = text;
    attribute.edited = true;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);

    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
  }

  dateRangeChanged(attribute: Attribute, text: any) {
    attribute.value = text;
    attribute.edited = true;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);

    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
  }

  skillChanged(attribute: Attribute, selectedSkill: AttributeDefinitionValueListItem) {
    attribute.edited = true;
    attribute.attributeDefinitionValueListItem = selectedSkill;

    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.updateQuestionsVisibility(attribute);
  }

  customDocumentUploaderChanged(attribute: Attribute, uploadedFiles: CustomDocumentUploadFile[]) {
    attribute.edited = true;

    let value = uploadedFiles
      .map((x) => x.fileName.trim())
      .join('|')
      .toString();
    attribute.value = value ?? null;
    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.blurred.emit(true);
  }

  multiSelectChanged(attribute: Attribute, event: MultiselectEvent) {
    attribute.edited = true;

    const attrDefinition = this.dataSource.attributeDefinitions.find(
      (x) => x.id == attribute.attributeDefinitionId
    );
    const selectedItems = event.selectedItems.map((x) =>
      attrDefinition.valueList.find((a) => a.id == x.id)
    );
    attribute.valueList = selectedItems;

    const attributeInForm = this.attributesForm.get(attribute.attributeDefinitionId);
    if (attribute.getValue()) {
      attributeInForm.setValue(true);
    } else {
      attributeInForm.setValue(false);
    }
    attributeInForm.markAsTouched();
    attributeInForm.updateValueAndValidity();
    this.updateQuestionsVisibility(attribute);
  }

  updateCheckboxAttributeDefinition(
    attribute: Attribute,
    valueListItem: AttributeDefinitionValueListItem,
    event: MatCheckboxChange
  ) {
    let editableAttribute = _.find(this._dataSource.editable, function (personAttribute) {
      return attribute.attributeDefinitionId == personAttribute.attributeDefinitionId;
    });

    attribute.edited = true;
    editableAttribute.edited = true;

    if (event.checked) {
      editableAttribute.setOrAddValue(valueListItem);
    } else {
      editableAttribute.removeValue(valueListItem.itemValue);
    }
    this.updateQuestionsVisibility(attribute);

    if (editableAttribute.required) {
      const attributeInForm = <UntypedFormArray>(
        this.attributesForm.get(attribute.attributeDefinitionId)
      );
      const collection = attributeInForm.value.controls;
      let valueListItemInForm = _.find(
        collection,
        (control) => control.value.id == valueListItem.id
      );

      valueListItemInForm.value.checked = event.checked;
      valueListItemInForm.markAsTouched();
      attributeInForm.updateValueAndValidity();
    }

    // Apply last — updateValueAndValidity re-runs validators and would wipe this error
    this.addCheckboxMaxSelectionsError(editableAttribute);
  }

  getCheckboxStatus(attribute: Attribute, valueListItem: AttributeDefinitionValueListItem) {
    const personAttribute = _.find(this._dataSource.editable, function (personAttribute) {
      return personAttribute.attributeDefinitionId == attribute.attributeDefinitionId;
    });

    const checked = _.find(
      personAttribute.valueList,
      function (currentValueListItem: AttributeDefinitionValueListItem) {
        return currentValueListItem.id == valueListItem.id;
      }
    );

    return checked != undefined;
  }

  getAttributeValueList(attribute: Attribute): AttributeDefinitionValueListItem[] {
    let type = _.find(
      this._dataSource.attributeDefinitions,
      (attrDef) => attrDef.id === attribute.attributeDefinitionId
    );
    return type.valueList;
  }
  private cachedMultiselectValue: Record<string, MultiselectItem[]> = {};

  getMultiselectValueList(attribute: Attribute): MultiselectItem[] {
    const type = this._dataSource.attributeDefinitions.find(
      (attrDef) => attrDef.id === attribute.attributeDefinitionId
    );
    //need to cache the dropdown for the attribute because the MultiselectComponent.items setter has logic we should try and not trigger
    if (
      this.cachedMultiselectValue[attribute.attributeDefinitionId]?.length != type.valueList.length
    ) {
      //do a deep comparison instead?
      this.cachedMultiselectValue[attribute.attributeDefinitionId] = type.valueList.map(
        (v) =>
          new MultiselectItem(
            v.id,
            v.itemValue,
            attribute.valueList.findIndex((a) => a.id == v.id) > -1
          )
      );
    }
    return this.cachedMultiselectValue[attribute.attributeDefinitionId];
  }

  get shortAnswerAttributeUiType(): AttributeUiType {
    return AttributeUiType.ShortAnswer;
  }

  get encryptedFieldAttributeUiType(): AttributeUiType {
    return AttributeUiType.EncryptedField;
  }

  get numberAttributeUiType(): AttributeUiType {
    return AttributeUiType.Number;
  }

  get dateAttributeUiType(): AttributeUiType {
    return AttributeUiType.Date;
  }

  get longAnswerAttributeUiType(): AttributeUiType {
    return AttributeUiType.LongAnswer;
  }

  get dropDownAttributeUiType(): AttributeUiType {
    return AttributeUiType.DropDown;
  }

  get radioButtonGroupAttributeUiType(): AttributeUiType {
    return AttributeUiType.RadioButtonGroup;
  }

  get checkboxAttributeUiType(): AttributeUiType {
    return AttributeUiType.Checkboxes;
  }

  get multiSelectAttributeUiType(): AttributeUiType {
    return AttributeUiType.MultiSelect;
  }

  get skillAttributeUiType(): AttributeUiType {
    return AttributeUiType.Skill;
  }

  get starRatingAttributeUiType(): AttributeUiType {
    return AttributeUiType.StarRating;
  }

  get documentUiType(): AttributeUiType {
    return AttributeUiType.Document;
  }

  get dateRangeUiType(): AttributeUiType {
    return AttributeUiType.DateRange;
  }

  get complexUiType(): AttributeUiType {
    return AttributeUiType.Complex;
  }

  onAddComplexValue(attribute: EditableAttribute) {
    let dialogRef = this.dialog.open(ComplexFieldEditorComponent, {
      data: { attribute: attribute, attributes: [], attributeEditorMode: this.mode }
    });

    dialogRef.afterClosed().subscribe((result: AttributesEditorDataSource) => {
      if (result) {
        attribute.edited = true;

        let complexFieldWrite = result.asComplexAttributeWrite(attribute);
        this.addOrUpdateComplexAttributeValue(attribute, complexFieldWrite, result.editable);
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0]
          });
        }
        this.accessibilityService.talk(`${attribute.name} added`);
      }
    });
  }

  onEditComplexValue(
    attribute: EditableAttribute,
    complexAttributeValue: FlatComplexAttributeValue
  ) {
    let editItemIndex = attribute.complexAttributeValues.indexOf(complexAttributeValue);
    var attrs: Attribute[] = [];
    complexAttributeValue.items.forEach((x) => {
      x.subFieldValues.forEach((y) => {
        let attr = Attribute.parse({
          value: y.value,
          type: y.type,
          attributeDefinitionValueListItem: y.attributeDefinitionValueListItem,
          attributeDefinitionId: attribute.subFieldList.find(
            (sub) => sub.id == y.subFieldDefinitionItemId
          ).subFieldDefinition.id
        });
        attrs.push(attr);
      });
    });
    let dialogRef = this.dialog.open(ComplexFieldEditorComponent, {
      data: { attribute: attribute, attributes: attrs, attributeEditorMode: this.mode }
    });

    dialogRef.afterClosed().subscribe((result: AttributesEditorDataSource) => {
      if (result) {
        attribute.edited = true;

        let complexFieldWrite = result.asComplexAttributeWrite(attribute);
        this.addOrUpdateComplexAttributeValue(
          attribute,
          complexFieldWrite,
          result.editable,
          editItemIndex
        );
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0]
          });
        }
        this.accessibilityService.talk(`${attribute.name} updated`);
      }
    });
  }

  onDeleteComplexValue(
    attribute: EditableAttribute,
    complexAttributeValue: FlatComplexAttributeValue
  ) {
    let dialogRef = this.dialog.open(ModalConfirmationComponent, {
      data: { action: `delete ${attribute.name}`, delete: true }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        attribute.edited = true;

        _.remove(attribute.complexAttributeValues, complexAttributeValue);
        this.addComplexRangeError(attribute);
        if (this.mode != AttributeEditorMode.Application) {
          this.dataSource.editable[
            this.dataSource.editable.findIndex(
              (x) => x.attributeDefinitionId == attribute.attributeDefinitionId
            )
          ] = attribute;
          this.addOrUpdateComplexValue.emit({
            attributeDefinitionId: attribute.attributeDefinitionId,
            writeModel: this.dataSource.asComplexAttributeWrites()[0]
          });
        }
        this.fieldUpdated.emit(attribute.attributeDefinitionId);
        this.accessibilityService.talk(`${attribute.name} deleted`);
      }
    });
  }

  dropDownAttributeDefinitionValueListItemComparer(
    option: AttributeDefinitionValueListItem,
    value: AttributeDefinitionValueListItem
  ): boolean {
    return option && value ? option.id == value.id : false;
  }

  isVisible(attribute: EditableAttribute) {
    const initVisibility = attribute.isVisible;
    if (attribute.hasCondition) {
      const checkResult = this.areConditionsMet(attribute.conditions);
      checkResult ? attribute.show() : this.resetValues(attribute);
    }
    if (initVisibility != attribute.isVisible) {
      this.onAttributeVisibilityChanged(attribute);
    }
    return attribute.isVisible;
  }

  isDisplayed(text: string): boolean {
    if (!!text && text.length > 110) {
      return true;
    }

    return false;
  }
  onDownloadFile(fileName: string, attribute: Attribute) {
    this.downloadFile.emit({
      fileName: fileName,
      attributeDefinitionId: attribute.attributeDefinitionId
    });
  }
  /** called after editing/adding a complex attribute */
  private addOrUpdateComplexAttributeValue(
    attribute: EditableAttribute,
    complexFieldWrite: ComplexAttributeWrite,
    definitions: EditableAttribute[],
    editItemIndex: number = -1
  ) {
    let groupId =
      attribute.complexAttributeValues.length > 0
        ? attribute.complexAttributeValues[attribute.complexAttributeValues.length - 1].groupId + 1
        : 1;
    let flatAtt = new FlatComplexAttributeValue();

    flatAtt.groupId = groupId;

    flatAtt.items = complexFieldWrite.complexSubFieldValues.map((v, index) => {
      let subFieldValues = [];
      let type = attribute.subFieldList.find(
        (x) => x.subFieldDefinitionId == v.subFieldItemValue.attributeDefinitionId
      ).subFieldDefinition.uiType;

      let name = attribute.subFieldList.find(
        (x) => x.subFieldDefinitionId == v.subFieldItemValue.attributeDefinitionId
      ).subFieldDefinition.name;
      let rank = index + 1;
      var attr = definitions.find(
        (a) => a.attributeDefinitionId == v.subFieldItemValue.attributeDefinitionId
      );
      if (type == AttributeUiType.MultiSelect || type == AttributeUiType.Checkboxes) {
        attr.valueList.forEach((vl) => {
          let subFieldValue = new ComplexAttributeSubFieldValue(
            v.subFieldDefinitionItemId,
            groupId,
            vl.itemValue,
            name,
            type,
            vl.rank,
            vl
          );
          subFieldValues.push(subFieldValue);
        });
      } else {
        let subFieldValue = new ComplexAttributeSubFieldValue(
          v.subFieldDefinitionItemId,
          groupId,
          attr.attributeDefinitionValueListItem
            ? attr.attributeDefinitionValueListItem.itemValue
            : attr.value,
          name,
          type,
          rank,
          attr.attributeDefinitionValueListItem
        );
        subFieldValues = [subFieldValue];
      }
      let item = new ComplexAttributeItems(
        v.subFieldDefinitionItemId,
        subFieldValues,
        name,
        type,
        rank
      );

      return item;
    });
    if (editItemIndex == -1) {
      attribute.complexAttributeValues.push(flatAtt);
    } else {
      attribute.complexAttributeValues[editItemIndex] = flatAtt;
    }
    this.addComplexRangeError(attribute);
    this.addComplexSubFieldRequiredError(attribute);

    this.fieldUpdated.emit(attribute.attributeDefinitionId);
  }
  private areConditionsMet(conditions: AttributeDefinitionCondition[]): boolean {
    let result = false;
    conditions.forEach((condition) => {
      let targetQuestion = this.dataSource.editable.find(
        (x) => x.attributeDefinitionId == condition.targetAttributeDefinitionId
      );
      if (!targetQuestion.isVisible) {
        result = false;
      } else if (_.intersection(condition.conditionValues, targetQuestion.values()).length > 0) {
        result = true;
      }
    });
    return result;
  }

  private updateQuestionsVisibility(attribute: Attribute) {
    this._dataSource.editable
      .filter((x) =>
        x.conditions?.find((x) => x.targetAttributeDefinitionId == attribute.attributeDefinitionId)
      )
      .forEach((attribute) => {
        this.isVisible(attribute);
        this.updateQuestionsVisibility(attribute);
      });
  }
  private resetValues(attribute: EditableAttribute) {
    attribute.hide();
    if (!this.attributesForm) return;
    if (attribute.allowsMultipleValues) {
      attribute.valueList = attribute.defaultValue
        ? [...(<AttributeDefinitionValueListItem[]>attribute.defaultValue)]
        : [];
      this.attributesForm.controls[attribute.attributeDefinitionId].setValue(
        attribute.isCheckboxType() ? this.getCheckboxes(attribute) : attribute.valueList
      );
    } else {
      this.attributesForm.controls[attribute.attributeDefinitionId].setValue(
        attribute.defaultValue
      );
      this.assignValueToAttribute(attribute);
    }

    this.attributesForm.get(attribute.attributeDefinitionId).markAsUntouched();
  }

  public getUploadPath(attribute: Attribute) {
    if (this.personDetailsMode) {
      return this.customDocumentService.getProfileDocumentUploadPath(
        this.entityId,
        attribute.attributeDefinitionId
      );
    }

    if (this.teamDetailsMode) {
      return {
        path: `teams/${this.entityId}/${attribute.attributeDefinitionId}/custom-document`,
        apiVersion: ApiVersions.v0_1
      };
    }

    return {
      path: `documents/${this.secureLinkId}/${attribute.attributeDefinitionId}/scan-upload-temporary`,
      apiVersion: ApiVersions.v0_1
    };
  }

  onFieldChanged(attributeDefinitionId: string) {
    this.fieldUpdated.emit(attributeDefinitionId);
  }

  private onAttributeVisibilityChanged(attribute: EditableAttribute) {
    this.onFieldChanged(attribute.attributeDefinitionId);
  }
}
